import { Dispatch } from 'redux';
import { useHistory } from 'react-router-dom';
import { IPartsSelectParam, showConfirm3DAction } from '@app/react/action';
import { ActionCreator } from 'typescript-fsa';
import type {
    PartsProduct,
    Category,
    SubCategory,
    ProductWithCategory,
} from '@app/react/const/type';
import { useOrderPathParams } from '@app/react/common/useOrderPathParams';
import { useEditOrderPathParams } from './useEditOrderPathParams';

export const filterProducts: (
    partsData: PartsProduct[] | null,
    categoryID: number,
    subcategoryID: number
) => PartsProduct[] = (partsData, categoryID, subcategoryID) => {
    if (partsData === null) {
        return [];
    }

    return partsData.filter((product) => {
        if (categoryID === 0 && subcategoryID === 0) {
            // all showing
            return true;
        } if (categoryID !== 0 && subcategoryID === 0) {
            return product.categoryId === categoryID;
        } if (categoryID === 0 && subcategoryID !== 0) {
            return product.subCategoryId === subcategoryID;
        }

        return (
            product.categoryId === categoryID
            && product.subCategoryId === subcategoryID
        );
    });
};

export const reduceProductWithCategory: (
    newPartsData: PartsProduct[] | null
) => ProductWithCategory = (newPartsData) => {
    if (newPartsData === null) {
        return [];
    }

    return newPartsData.reduce((acc, product) => {
        let c = acc.find((item) => item.categoryId === product.categoryId);
        if (c === undefined) {
            const item = {
                categoryId: product.categoryId,
                categoryName: product.categoryName,
                subCategoryData: [
                    {
                        subCategoryId: product.subCategoryId,
                        subCategoryName: product.subCategoryName,
                        products: [],
                    },
                ],
            };
            acc.push(item);
            c = item;
        }

        const sc = c.subCategoryData.find(
            (item) => item.subCategoryId === product.subCategoryId,
        );
        if (sc === undefined) {
            c.subCategoryData.push({
                subCategoryId: product.subCategoryId,
                subCategoryName: product.subCategoryName,
                products: [product],
            });
        } else {
            sc.products.push(product);
        }

        return acc;
    }, [] as ProductWithCategory);
};

// dispatchFunction
export const submitPartSelect = (
    dispatch: Dispatch,
    action: ActionCreator<IPartsSelectParam>,
    param: IPartsSelectParam,
): void => {
    dispatch(action(param));
};

type GoParts = (partsName?: string | null) => void;

export const useHistoryGoParts = (): GoParts => {
    const history = useHistory();
    const { arrowType, orderType } = useOrderPathParams();
    return (partsName: string | null = null): void => {
        let url = `/${arrowType}/${orderType}/parts`;
        if (partsName) {
            url = `${url}/${partsName}`;
        }
        history.push(url);
    };
};

export const useHistoryGoPartsEdit = (): GoParts => {
    const history = useHistory();
    const { cartId, arrowType, orderType } = useEditOrderPathParams();
    return (partsName: string | null = null): void => {
        let url = `/${cartId}/${arrowType}/${orderType}/parts`;
        if (partsName) {
            url = `${url}/${partsName}`;
        }
        history.push(url);
    };
};

type FSCBC = (
    categoryData: Array<Category> | null | undefined,
    selectCategoryId: number
) => Array<SubCategory>;
export const filterSubCategoryByCategory: FSCBC = (
    categoryData,
    selectCategoryId,
) => (
        categoryData?.reduce((acc: Array<SubCategory>, category) => {
            if (selectCategoryId === 0) {
                return acc.concat(category.subCategoryData);
            }

            if (category.categoryId === selectCategoryId) {
                return acc.concat(category.subCategoryData);
            }

            return acc;
        }, [] as SubCategory[]) || []
    );

const showEcHeaderNaviRole = () => {
    const header = document.getElementById('ec-headerNaviRole');
    if (header === null) {
        console.error('show ec-cube header, but header not found', header);
        return;
    }
    header.classList.remove('hidden');
};

const adjustHeightWithHeader = (adjust: boolean) => {
    const partsContainer = document.getElementById('parts-container-buy-view');
    if (partsContainer === null) {
        console.error(
            'show ec-cube header, but parts container not found',
            partsContainer,
        );
        return;
    }
    if (adjust) {
        partsContainer.classList.add('parts-container--non-header');
        return;
    }
    partsContainer.classList.remove('parts-container--non-header');
};

const hideEcHeaderNaviRole = () => {
    const header = document.getElementById('ec-headerNaviRole');
    if (header === null) {
        console.error('hide ec-cube header, but header not found', header);
        return;
    }
    header.classList.add('hidden');
};

const showEcHeaderNaviRoleRight = () => {
    const nav = document.getElementById('ec-headerNaviRole__nav');
    const ham = document.getElementById('ec-headerNaviRole__hamburger-menu');
    if (nav === null || ham === null) {
        console.error(
            'show ec-cube right header, but nav and hamburger-menu not found',
        );
        return;
    }

    nav.classList.remove('hidden');
    ham.classList.remove('hidden');
};

const hideEcHeaderNaviRoleRight = () => {
    const nav = document.getElementById('ec-headerNaviRole__nav');
    const ham = document.getElementById('ec-headerNaviRole__hamburger-menu');
    if (nav === null || ham === null) {
        console.error(
            'show ec-cube right header, but nav and hamburger-menu not found',
        );
        return;
    }

    nav.classList.add('hidden');
    ham.classList.add('hidden');
};

export const showEcHeaderNavi = (
    isDesktop: boolean,
    isMobile: boolean,
): void => {
    if (isDesktop) {
        showEcHeaderNaviRoleRight();
        showEcHeaderNaviRole();
        adjustHeightWithHeader(false);
    }
    if (isMobile) {
        showEcHeaderNaviRoleRight();
        showEcHeaderNaviRole();
        adjustHeightWithHeader(false);
    }
};

export const hideEcHeaderNavi = (
    isDesktop: boolean,
    isMobile: boolean,
    dispatch: Dispatch,
): void => {
    if (isDesktop) {
        dispatch(showConfirm3DAction({ visible: true }));
        hideEcHeaderNaviRoleRight();
        showEcHeaderNaviRole();
        adjustHeightWithHeader(false);
    }
    if (isMobile) {
        dispatch(showConfirm3DAction({ visible: false }));
        hideEcHeaderNaviRole();
        adjustHeightWithHeader(true);
        showEcHeaderNaviRoleRight();
    }
};

export const addFullEcHeaderNaviRole = (): void => {
    const header = document.getElementById('ec-headerNaviRole');
    if (header === null) {
        console.error('show ec-cube header, but header not found', header);
        return;
    }
    header.classList.add('header-full-width');
};

export const removeFullEcHeaderNaviRole = (): void => {
    const header = document.getElementById('ec-headerNaviRole');
    if (header === null) {
        console.error('show ec-cube header, but header not found', header);
        return;
    }
    header.classList.remove('header-full-width');
};
